import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import { ChevronLeft, ChevronDown } from 'lucide-react';

// Keep in sync with MemberScore.js
const CATS = {
  strength:           { color: '#EF4444', name: '근력' },
  cardio:             { color: '#3B82F6', name: '심폐지구력' },
  body_composition:   { color: '#A78BFA', name: '체성분' },
  body_shape:         { color: '#22C55E', name: '체형' },
  muscular_endurance: { color: '#F97316', name: '근지구력' },
  inbody:             { color: '#A78BFA', name: '체성분' },
  posture:            { color: '#22C55E', name: '체형' },
};
const getCatName = (c) => CATS[c]?.name || c || '-';
const getCatColor = (c) => CATS[c]?.color || '#52525B';

const EXERCISE_LABELS = {
  deadlift: '데드리프트', squat: '스쿼트', bench_press: '벤치프레스', shoulder_press: '숄더프레스',
  pushup: '푸시업', situp: '싯업',
  barbell_squat_endurance: '바벨 스쿼트', barbell_deadlift_endurance: '바벨 데드리프트',
  barbell_shoulder_press_endurance: '바벨 숄더프레스',
  plank: '플랭크', wall_squat: '월 스쿼트',
  amrap1: 'AMRAP1', amrap2: 'AMRAP2', amrap3: 'AMRAP3', amrap4: 'AMRAP4',
  running: '러닝', rowing: '로잉',
  inbody: '인바디', posture: '자세평가',
};
const getExerciseLabel = (c) => EXERCISE_LABELS[c] || c;

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '-';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const MemberMeasurements = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/app/login'); return; }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const res = await apiClient.get(`/app/measurements?token=${token}`);
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => new Date(b.measured_at || 0) - new Date(a.measured_at || 0));
      setMeasurements(list);
    } catch (e) {
      console.error('Failed to fetch measurements:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (idx) => {
    setOpenIdx(prev => (prev === idx ? null : idx));
  };

  const buildDetailRows = (m) => {
    const rm = m.raw_metrics || {};
    const cm = m.computed_metrics || {};
    const ex = m.exercise;
    const cat = m.category;

    // 근력 3종(데드/스쿼트/숄프/벤치) — 무게 / 반복 / 추정1RM / 상대근력
    const isStrengthCore = cat === 'strength'
      && ['deadlift', 'squat', 'shoulder_press', 'bench_press'].includes(ex);
    if (isStrengthCore) {
      const rows = [];
      const oneRm = cm.one_rm ?? rm['1rm_estimated'];
      let relStrength = cm.relative_strength;
      if (relStrength === undefined && oneRm !== undefined && rm.bw_kg) {
        relStrength = oneRm / rm.bw_kg;
      }
      if (rm.weight_kg !== undefined) rows.push(['무게', `${rm.weight_kg}kg`]);
      if (rm.reps !== undefined) rows.push(['반복', `${rm.reps}회`]);
      if (oneRm !== undefined) rows.push(['추정 1RM', `${Number(oneRm).toFixed(1)}kg`]);
      if (relStrength !== undefined) rows.push(['상대근력', Number(relStrength).toFixed(2)]);
      return rows;
    }

    // 근지구력 — 시간 / 기록 (AMRAP은 원본 X.YY 표시)
    if (cat === 'muscular_endurance') {
      const rows = [];
      const isAmrap = ['amrap1', 'amrap2', 'amrap3', 'amrap4'].includes(ex);
      if (isAmrap) {
        const mins = cm.amrap_minutes ?? rm.amrap_minutes;
        const rec = cm.amrap_record ?? rm.amrap_record;
        if (mins !== undefined) rows.push(['시간', `${mins}min`]);
        if (rec !== undefined) rows.push(['기록', `${rec}`]);
        // AMRAP3: 사용무게 + 최종점수 표시 (무게보정은 표시하지 않음)
        if (ex === 'amrap3') {
          const wkg = cm.weight_kg ?? rm.weight_kg;
          if (wkg !== undefined && wkg !== null) rows.push(['사용무게', `${wkg}kg`]);
          const finalSc = cm.final_score;
          if (finalSc !== undefined && finalSc !== null) {
            rows.push(['최종점수', `${Number(finalSc).toFixed(0)}점`]);
          }
        }
        return rows;
      }
      const sec = rm.seconds ?? rm.duration_seconds ?? rm.time_seconds;
      // 시간 데이터가 raw_metrics에 없으면 종목별 기본 측정 시간을 폴백으로 사용
      const DEFAULT_MINUTES = { pushup: 3, situp: 3 };
      const formatTime = (s) => {
        if (s === undefined || s === null) return null;
        if (s % 60 === 0) return `${s / 60}min`;
        return `${Math.floor(s / 60)}분 ${s % 60}초`;
      };
      if (sec !== undefined) {
        rows.push(['시간', formatTime(sec) || `${sec}초`]);
      } else if (DEFAULT_MINUTES[ex]) {
        rows.push(['시간', `${DEFAULT_MINUTES[ex]}min`]);
      }
      if (rm.reps !== undefined) rows.push(['기록', `${rm.reps}회`]);
      else if (rm.input_load_kg !== undefined) rows.push(['기록', `${rm.input_load_kg}kg`]);
      if (cm.base_load_kg !== undefined) {
        const diff = cm.load_diff_pct;
        const diffStr = diff !== undefined ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
        rows.push(['기준 중량', `${cm.base_load_kg}kg${diffStr}`]);
      }
      return rows;
    }

    // 심폐지구력 - 로잉(Performance Time 기반) — 거리/시간/퍼포먼스 기록/최종 점수
    if (cat === 'cardio' && ex === 'rowing') {
      const rows = [];
      if (rm.distance_m !== undefined) rows.push(['거리', `${rm.distance_m}m`]);
      if (rm.duration_seconds !== undefined) {
        const mins = Math.floor(rm.duration_seconds / 60);
        const secs = rm.duration_seconds % 60;
        rows.push(['시간', `${mins}분 ${secs}초`]);
      }
      const perfFmt = cm.performance_time_formatted
        ?? (cm.performance_time_seconds !== undefined
              ? `${Math.floor(cm.performance_time_seconds / 60)}:${String(Math.round(cm.performance_time_seconds % 60)).padStart(2, '0')}`
              : null);
      if (perfFmt) rows.push(['퍼포먼스 기록', perfFmt]);
      return rows;
    }

    // 심폐지구력 - 러닝 — 거리/시간/VO2max (기존 유지)
    if (cat === 'cardio' && ex === 'running') {
      const rows = [];
      if (rm.distance_m !== undefined) rows.push(['거리', `${rm.distance_m}m`]);
      if (rm.duration_seconds !== undefined) {
        const mins = Math.floor(rm.duration_seconds / 60);
        const secs = rm.duration_seconds % 60;
        rows.push(['시간', `${mins}분 ${secs}초`]);
      }
      if (cm.vo2 !== undefined) rows.push(['VO2max', `${cm.vo2}`]);
      return rows;
    }

    // 기타 카테고리 — 기존 표시 유지
    const rows = [];
    if (rm.weight_kg !== undefined && cat !== 'inbody') rows.push(['무게', `${rm.weight_kg}kg`]);
    if (rm.reps !== undefined) rows.push(['반복 횟수', `${rm.reps}회`]);
    if (rm.input_load_kg !== undefined) rows.push(['사용 중량', `${rm.input_load_kg}kg`]);
    if (rm.seconds !== undefined) rows.push(['유지 시간', `${rm.seconds}초`]);
    if (rm.distance_m !== undefined) rows.push(['거리', `${rm.distance_m}m`]);
    if (rm.duration_seconds !== undefined) {
      const mins = Math.floor(rm.duration_seconds / 60);
      const secs = rm.duration_seconds % 60;
      rows.push(['시간', `${mins}분 ${secs}초`]);
    }
    if (rm.time_seconds !== undefined) {
      const mins = Math.floor(rm.time_seconds / 60);
      const secs = rm.time_seconds % 60;
      rows.push(['시간', `${mins}분 ${secs}초`]);
    }
    if (cat === 'inbody' && rm.weight_kg !== undefined) rows.push(['체중', `${rm.weight_kg}kg`]);
    if (rm.body_fat_pct !== undefined) rows.push(['체지방률', `${rm.body_fat_pct}%`]);
    if (rm.body_fat_percentage !== undefined) rows.push(['체지방률', `${rm.body_fat_percentage}%`]);
    if (rm.skeletal_muscle_kg !== undefined) rows.push(['골격근량', `${rm.skeletal_muscle_kg}kg`]);
    if (rm.skeletal_muscle_mass !== undefined) rows.push(['골격근량', `${rm.skeletal_muscle_mass}kg`]);
    if (rm.inbody_score !== undefined) rows.push(['인바디 점수', `${rm.inbody_score}`]);
    if (rm.posture_score !== undefined) rows.push(['자세 점수', `${rm.posture_score}`]);
    if (cm.vo2 !== undefined) rows.push(['VO2max', `${cm.vo2}`]);
    return rows;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0F1115]">
        <div className="text-[#52525B]">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24 text-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 pt-12 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A1D23] active:scale-95 transition-transform"
          data-testid="back-btn"
        >
          <ChevronLeft className="h-5 w-5 text-[#A1A1AA]" />
        </button>
        <h1 className="text-lg font-bold">측정 기록</h1>
        <span className="ml-auto text-xs text-[#52525B]">
          {measurements.length > 0 ? `총 ${measurements.length}건` : ''}
        </span>
      </div>

      <div className="px-4">
        {measurements.length === 0 ? (
          <div className="bg-[#1A1D23] rounded-2xl p-10 text-center mt-4" data-testid="empty-state">
            <p className="text-[#A1A1AA] text-sm font-medium">아직 측정 기록이 없어요</p>
            <p className="text-[#52525B] text-xs mt-2">운동을 시작하면 기록이 표시됩니다</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="measurement-list">
            {measurements.map((m, idx) => {
              const isOpen = openIdx === idx;
              const color = getCatColor(m.category);
              const rows = isOpen ? buildDetailRows(m) : [];
              return (
                <div
                  key={idx}
                  className={`bg-[#1A1D23] rounded-2xl overflow-hidden transition-colors ${
                    isOpen ? 'ring-1 ring-[#FF7A00]/30' : ''
                  }`}
                  data-testid={`measurement-item-${idx}`}
                >
                  {/* Card header (clickable) */}
                  <button
                    onClick={() => toggleItem(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#22252E]/60 transition ${
                      isOpen ? 'bg-[#22252E]/40' : ''
                    }`}
                  >
                    <div className="w-0.5 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium truncate">
                          {m.exercise ? getExerciseLabel(m.exercise) : getCatName(m.category)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                          style={{ backgroundColor: `${color}18`, color }}
                        >
                          {getCatName(m.category)}
                        </span>
                        <span className="text-[10px] text-[#52525B]">{formatDate(m.measured_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-base font-bold tabular-nums" style={{ color }}>
                        {m.score?.toFixed(0) || '-'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-[#52525B] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </button>

                  {/* Expanded detail (slide-down) */}
                  <div
                    className="grid transition-all duration-200 ease-out"
                    style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                  >
                    <div className="overflow-hidden">
                      <div className="px-4 pb-4 pt-1 border-t border-[#22252E]">
                        {rows.length === 0 ? (
                          <p className="text-[#52525B] text-xs text-center py-4">세부 정보가 없습니다</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3" data-testid={`detail-${idx}`}>
                            {rows.map(([label, value], i) => (
                              <div key={i} className="flex justify-between text-xs">
                                <span className="text-[#52525B]">{label}</span>
                                <span className="text-[#A1A1AA] font-medium">{value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav activeTab="score" />
    </div>
  );
};

export default MemberMeasurements;
